import { OrgId, PaymentMethodResponse, OrgResponse } from '@business/entities/organization';
import { SubscriptionId, SubscriptionResponse } from '@business/entities/project/SubscriptionResponse';
import { CommonService, StripeService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useHistory } from '@core/router';
import { formControl } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { R } from '@core/utils/r';
import { tapError } from '@core/utils/rxjs/operators';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { Badge, Card, CardBody, CardTitle, Col, DropdownMenu, DropdownToggle, Row, Spinner, UncontrolledDropdown } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { OrganizationPageBloc } from '../../OrganizationPageBloc';
import { openCancelSubscriptionModal } from './CancelSubscriptionModal';
import { InvoiceLineTooltip } from './InvoiceLineTooltip';
import { openManualSupportHourModal } from './ManualSupportHourModal';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { Config } from '@config';
import { openChangeHistoryModal } from '@modules/subscription/ChangeHistoryModal';
export interface OrgSubscriptionsProps {
  orgId: OrgId;
  subscriptions: SubscriptionResponse[];
  methods: PaymentMethodResponse[];
  bloc: OrganizationPageBloc;
  canProcess: boolean;
  organization: OrgResponse;
  isModal?: boolean;
}

export const OrgSubscriptions = memo((props: OrgSubscriptionsProps) => {
  const { orgId, subscriptions, bloc, isModal, organization } = props;
  const control = useMemo(() => formControl<number>({ initialValue: 0 }), []);
  const [active, setActive] = useState(true);
  const [isSyncingSub, setSyncingSub] = useState(false);

  const fullLoading = useLoadingState(bloc.fullLoading);

  const onSynSub = useCallback(
    (orgId: OrgId, id: SubscriptionId) => {
      setSyncingSub(true);
      bloc
        .syncSubscription(orgId, id)
        .pipe(
          tap(() => setSyncingSub(false)),
          tapError(() => setSyncingSub(false)),
        )
        .subscribe();
    },
    [bloc],
  );

  const onSubmitManualSupportHours = useCallback(
    (expiredAt: number, supportHours: number, notes: string, subId: SubscriptionId) => {
      return bloc.manualSupportHours(orgId, {
        subId,
        expiredAt,
        supportHours,
        notes,
      });
    },
    [bloc, orgId],
  );

  const history = useHistory();
  const isStage = useMemo(() => Config.IS_STAGE, []);

  const columns: GCColumn<SubscriptionResponse>[] = useMemo(
    () => [
      {
        id: 'name',
        name: 'Title',
        sortField: 'name',
        minWidth: '150px',
        sortable: true,
        cell: row => (
          <Fragment>
            <InvoiceLineTooltip lineItems={row.lineItems} title={row.name} />
          </Fragment>
        ),
      },
      {
        name: 'Amount',
        sortField: 'total',
        cell: item => Formatter.formatCurrency(item.amount, organization.currency),
        minWidth: '80px',
      },
      {
        name: 'Interval',
        sortField: 'balance',
        cell: item => R.upperFirst(item.cycle),
        minWidth: '80px',
      },
      {
        name: 'Payment Status',
        sortField: 'paymentStatus',
        cell: item => (
          <>
            {item.isStripe && (
              <Badge pill className={`badge-${StripeService.getSubscriptionStatusColor(item.stripeStatus)}  font-size-12 px-2 py-1`}>
                {StripeService.getSubscriptionStatusName(item.stripeStatus)}
              </Badge>
            )}
            {!item.isStripe && (
              <Badge pill className={`badge-${item.isOverdue || item.isCanceled ? 'danger' : 'info'}  font-size-12 px-2 py-1`}>
                {item.isCanceled && 'Canceled'}
                {!item.isCanceled && item.isOverdue && 'Overdue'}
                {!item.isCanceled && !item.isOverdue && 'Active'}
              </Badge>
            )}
          </>
        ),
        minWidth: '80px',
      },
      {
        name: 'Start Date',
        sortField: 'startDate',
        cell: item => Formatter.formatTime(item.startDate),
        minWidth: '80px',
      },
      {
        name: 'Next Bill Date',
        sortField: 'nextDate',
        cell: item => (item.nextDate ? Formatter.formatTime(item.nextDate) : 'N/A'),
        minWidth: '80px',
      },
      {
        omit: active,
        name: 'Canceled on',
        sortField: 'canceledAt',
        cell: item => Formatter.formatTime(item.canceledAt),
        minWidth: '80px',
      },
      {
        omit: !active,
        name: 'Action',
        id: 'action',
        width: '80px',
        cell: (row: SubscriptionResponse) => (
          <UncontrolledDropdown>
            <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
              <i className="bx bx-dots-horizontal-rounded"></i>
            </DropdownToggle>

            <DropdownMenu container="body" className="dd-menu_outside">
              <a
                className={`dropdown-item`}
                onClick={() => {
                  if (isModal) {
                    return (window.location.href = `/subscriptions/edit/${orgId}/${row.id}`);
                  }
                  history.push(`/subscriptions/edit/${orgId}/${row.id}`);
                }}
              >
                Edit Subscription
              </a>

              {isStage && (
                <a
                  className={`dropdown-item`}
                  onClick={() => {
                    openChangeHistoryModal({ subId: row.id, orgId });
                  }}
                >
                  Change History
                </a>
              )}
              {isStage && (
                <a
                  className={`dropdown-item`}
                  onClick={() => {
                    if (isModal) {
                      return (window.location.href = `/subscriptions/change/${orgId}/${row.id}`);
                    }
                    history.push(`/subscriptions/change/${orgId}/${row.id}`);
                  }}
                >
                  Change Subscription
                </a>
              )}

              <a
                className="dropdown-item"
                onClick={() => {
                  if (isModal) {
                    return (window.location.href = `/subscriptions/one-time-payment/${orgId}/${row.id}`);
                  }
                  history.push(`/subscriptions/one-time-payment/${orgId}/${row.id}`);
                }}
              >
                One Time Payment
              </a>

              {/* <a
                className="dropdown-item"
                onClick={() =>
                  openChangePaymentModal({ methods, methodId: row.paymentMethodId, onSubmit: methodId => bloc.changePaymentMethod(row.id, methodId) })
                }
              >
                Change Payment Method
              </a> */}
              <a
                className="dropdown-item"
                onClick={() =>
                  openManualSupportHourModal({
                    invoiceDate: row.invoiceDate,
                    onSubmit: (expiredAt: number, supportHours: number, notes: string) =>
                      onSubmitManualSupportHours(expiredAt, supportHours, notes, row.id),
                  })
                }
              >
                Add {gcLabel('Support')} Hours
              </a>
              <a className={`dropdown-item ${isSyncingSub && 'disabled'}`} onClick={() => onSynSub(orgId, row.id)}>
                Sync Subscription
                {!!isSyncingSub && <Spinner className="ml-2" size="sm" color="primary" />}
              </a>
              {row.canResend && (
                <a className="dropdown-item" onClick={() => bloc.resendLastInvoice(row.id).subscribe()}>
                  Resend Last Invoice
                </a>
              )}
              <div className="dropdown-divider"></div>
              <a
                className="dropdown-item text-danger"
                onClick={() => openCancelSubscriptionModal({ onSubmit: reason => bloc.cancelSubscription(orgId, row.id, reason) })}
              >
                Cancel Subscription
              </a>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [active, organization.currency, isSyncingSub, isModal, history, orgId, onSubmitManualSupportHours, onSynSub, bloc],
  );

  const activeItems = CommonService.selectItems(
    ['Active', 'Canceled'],
    i => i,
    (_, index) => index,
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          <div className="d-flex align-items-center">
            Subscription
            {fullLoading.loading && <Spinner className="ml-2 font-size-12" size="sm" color="primary" />}
          </div>

          <Row style={{ width: 330 }}>
            <Col>
              <FormSelect control={control} items={activeItems} onChange={val => setActive(!val)} />
            </Col>
            <Col>
              <Button
                intent="primary"
                onClick={() => {
                  if (isModal) {
                    return (window.location.href = `/subscriptions/create-subscription/${orgId}`);
                  }
                  history.push(`/subscriptions/create-subscription/${orgId}`);
                }}
              >
                Add Subscription
              </Button>
            </Col>
          </Row>
        </CardTitle>
        {!fullLoading.loading && (
          <Fragment>
            {(!active || !!subscriptions.length) && (
              <GCTable columns={columns} data={subscriptions.filter(s => s.isCanceled === !active)} noSearch pagination={false} subHeader={false} />
            )}
            {active && subscriptions.length === 0 && <div>The customer is not signed up for any subscriptions</div>}
          </Fragment>
        )}
      </CardBody>
    </Card>
  );
});
